import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../auth/useAuth";
import { apiClient } from "../api/ApiClient";
import type { SystemUser } from "../models/SystemUser";
import type { InventoryWareHouse } from "../models/InventoryWareHouse";
import type { PagedResult } from "../models/PagedResult";
import { WarehouseContext } from "./WarehouseContext";
export default function WarehouseProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state } = useAuth();
  const user = state.User;
  const identity =
    state.IsAuthenticated && user
      ? `${user.Tenant ?? ""}:${user.Code ?? user.MailAddress}`
      : "";
  const key = `pantrystack.operatingWarehouse:${identity}`;
  const previousIdentity = useRef("");
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<{
    identity: string;
    warehouses: InventoryWareHouse[];
    selected?: InventoryWareHouse;
    loading: boolean;
    error: string;
  }>({ identity: "", warehouses: [], loading: false, error: "" });
  useEffect(() => {
    if (previousIdentity.current && previousIdentity.current !== identity)
      localStorage.removeItem(
        `pantrystack.operatingWarehouse:${previousIdentity.current}`,
      );
    previousIdentity.current = identity;
    const controller = new AbortController();
    if (!identity) {
      setData({ identity: "", warehouses: [], loading: false, error: "" });
      return () => controller.abort();
    }
    setData({ identity, warehouses: [], loading: true, error: "" });
    void (async () => {
      if (!user?.Code)
        throw new Error(
          "The login response needs a user code to load warehouse assignments.",
        );
      const current = (
        await apiClient.get<SystemUser>(
          `authentication/SearchWithCode/${encodeURIComponent(user.Code)}`,
          {
            signal: controller.signal,
            showBusyIndicator: false,
            showError: false,
          },
        )
      ).data;
      if (!current.IsActive)
        throw new Error(
          "This account is inactive. Sign out and contact an administrator.",
        );
      const codes = [...new Set(current.WareHouses ?? [])];
      const admin = user.IsTenantAdmin === true;
      const warehouses: InventoryWareHouse[] = [];
      if (admin || codes.length)
        for (let skip = 0; ;) {
          const page = (
            await apiClient.post<PagedResult<InventoryWareHouse>>(
              `inventory-warehouse/Search/${skip}/200`,
              {
                Filters: [
                  ...(admin
                    ? []
                    : [{ Field: "Code", Operator: "In", Value: codes }]),
                  { Field: "Status", Operator: "EQ", Value: "Active" },
                ],
                Condition: "And",
              },
              {
                signal: controller.signal,
                showBusyIndicator: false,
                showError: false,
              },
            )
          ).data;
          warehouses.push(
            ...page.Data.filter(
              (x) => (admin || codes.includes(x.Code)) && x.Status === "Active",
            ),
          );
          skip += page.Data.length;
          if (!page.Data.length || skip >= page.TotalRecords) break;
        }
      if (controller.signal.aborted) return;
      const saved = localStorage.getItem(key);
      const selected =
        warehouses.length === 1
          ? warehouses[0]
          : warehouses.find((x) => x.Code === saved);
      if (selected) localStorage.setItem(key, selected.Code);
      else localStorage.removeItem(key);
      setData({ identity, warehouses, selected, loading: false, error: "" });
    })().catch((error) => {
      if (!controller.signal.aborted)
        setData({
          identity,
          warehouses: [],
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "Could not load your warehouse assignments. Retry or contact an administrator.",
        });
    });
    return () => controller.abort();
  }, [identity, revision]);
  const current =
    data.identity === identity
      ? data
      : {
          identity,
          warehouses: [],
          loading: !!identity,
          error: "",
          selected: undefined,
        };
  const SelectWarehouse = (code: string) => {
    const selected = current.warehouses.find((x) => x.Code === code);
    if (!selected) return;
    localStorage.setItem(key, code);
    setData({ ...current, selected });
  };
  return (
    <WarehouseContext.Provider
      value={{
        Warehouse: current.selected,
        Warehouses: current.warehouses,
        Loading: current.loading,
        Error: current.error,
        SelectWarehouse,
        Reload: () => setRevision((x) => x + 1),
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
}
